import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GlobalStore from "@stores/GlobalStore";
import Loader from "@components/LoaderComponent/loader";
import Show from "@components/Icons/Show";
import Hide from "@components/Icons/Hide";
import LanguageSelector from "@components/Icons/LanguageSelector";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import ReportsModalStore from "@stores/ReportsModalStore";
import { GetReportTypes } from "@/helpers/helpers";
import TailwindDropdown from "@components/TailwindDropdown";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";

import { TIMEOUT_VALUE, CALLBACK_TIMEOUT } from "./constants";

// Separate component for particles
const ParticlesBackground = React.memo(() => {
  const [init, setInit] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  if (!init) return null;

  return (
    <Particles
      id="tsparticles"
      className="absolute inset-0"
      options={{
        background: {
          color: {
            value: "#f3f4f6",
          },
        },
        fpsLimit: 120,
        interactivity: {
          events: {
            onClick: {
              enable: true,
              mode: "push",
            },
            onHover: {
              enable: false,
              mode: "repulse",
            },
            resize: true,
          },
          modes: {
            push: {
              quantity: 1,
            },
            repulse: {
              distance: 200,
              duration: 0.4,
            },
          },
        },
        particles: {
          color: {
            value: "#12bdce",
          },
          links: {
            color: "#0d7d8b",
            distance: 150,
            enable: true,
            opacity: 0.6,
            width: 1,
          },
          move: {
            direction: "none",
            enable: true,
            outModes: {
              default: "bounce",
            },
            random: false,
            speed: 0.2,
            straight: false,
          },
          number: {
            density: {
              enable: true,
              area: 800,
            },
            value: 100,
          },
          opacity: {
            value: 0.7,
          },
          shape: {
            type: "circle",
          },
          size: {
            value: { min: 1, max: 3 },
          },
        },
        detectRetina: true,
      }}
    />
  );
});

ParticlesBackground.displayName = "ParticlesBackground";

const LoginPage = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState("2222");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { t: translate, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || "en");

  const validate = () => {
    const errors = {};
    if (!username) errors.username = translate("login.username-required");
    if (!password) errors.password = translate("login.password-required");
    return errors;
  };

  const tenants = [
    { key: "2222", value: "Kudu" },
    { key: "6666", value: "Domino's" },
    { key: "0001", value: "Tenant M" },
    { key: "0002", value: "Tenant A" },
    { key: "1003", value: "Lulu" },
    { key: "1000", value: "Ikea (UAT only)" },
    { key: "1005", value: "ABP (UAT only)" },
    { key: "1006", value: "RetCon" },
    { key: "1007", value: "VanCon" },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});
    const validationErrors = validate();
    if (!!Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    // Simulate login API call
    setTimeout(() => {
      if (username === "admin" && password === "admin") {
        // call GetReportTypes api
        setTimeout(() => {
          GetReportTypes((response) => {
            ReportsModalStore.setLocalStorageReports(response.ReportTypes);
          });
        }, TIMEOUT_VALUE);

        GlobalStore.setGlobalSettings({
          ttpUserId: "2222_USER_1",
          languageCode: language,
          ttpTenantCode: tenant,
          callback: () => {
            setTimeout(() => {
              const lastLocation = localStorage.getItem("lastLocation");
              localStorage.removeItem("lastLocation");
              navigate(lastLocation || "/"); // Redirect to the home page after login
            }, CALLBACK_TIMEOUT);
          },
        });
      } else {
        setErrors({ submit: translate("login.invalid-credentials") });
      }
      setIsSubmitting(false);
    }, TIMEOUT_VALUE);
  };

  const handleLanguageChange = () => {
    const selectedLanguage = language === "en" ? "ar" : "en";
    setLanguage(selectedLanguage);
    setErrors({});

    i18n.changeLanguage(selectedLanguage);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 relative">
      <ParticlesBackground />
      <div
        className={clsx(
          "w-full max-w-md p-8 space-y-8 bg-white rounded-xl shadow-md relative z-10",
          errors.submit && "animate-jiggle",
          isSubmitting && "animate-pulse"
        )}
      >
        <div className="flex flex-col items-top gap-5">
          <img
            src="./images/logo/SwittleLogo.png"
            alt="logo"
            className="h-6 mx-auto"
          />
          <span className="text-xl font-semibold text-center w-full block text-gray-600">
            {translate("login.intro")}
          </span>
        </div>
        <div className="flex items-center justify-between gap-5">
          <h2 className="text-xl font-bold ">{translate("login.title")}</h2>
          <div className="flex gap-2">
            <label className="flex mt-6 text-sm font-medium text-gray-700">
              {translate("login.tenant")}
            </label>
            <TailwindDropdown
              data={tenants}
              selectedValue={tenant}
              setActiveTab={setTenant}
              className="min-w-28 whitespace-nowrap text-right items-end border-none"
            />
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={errors.username && "animate-jiggle"}>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700"
            >
              {translate("login.username")}
            </label>
            <input
              id="username"
              name="username"
              type="text"
              onChange={(e) => setUsername(e.target.value)}
              value={username}
              className="w-full px-3 py-2 mt-1 border rounded shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
            {errors.username && (
              <div className="mt-2 text-sm text-red-600">{errors.username}</div>
            )}
          </div>
          <div className={errors.password && "animate-jiggle"}>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              {translate("login.password")}
            </label>
            <div
              key={`showPassword-${showPassword}`}
              className="w-full px-3 py-2 mt-1 border rounded shadow-sm focus:ring-primary-500 focus:border-primary-500 flex gap-2"
            >
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                className="w-full outline-none"
              />
              {showPassword ? (
                <Show
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                />
              ) : (
                <Hide
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                />
              )}
            </div>
            {errors.password && (
              <div className="mt-2 text-sm text-red-600">{errors.password}</div>
            )}
          </div>

          {errors.submit && (
            <div className="mt-2 text-sm text-red-600">{errors.submit}</div>
          )}
          <div className="flex justify-between items-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-content flex gap-2 items-center justify-center px-4 py-2 font-medium text-white bg-brand-600 rounded hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {isSubmitting
                ? translate("login.logging-in")
                : translate("login.login")}
              {isSubmitting && <Loader size="5" />}
            </button>
            <a href="#" className="text-sm text-brand-500 hover:underline">
              {translate("login.forgot-password")}
            </a>
          </div>
        </form>
      </div>
      <div
        className={clsx(
          "fixed bottom-2 z-20",
          language === "ar" ? "right-2" : "left-2"
        )}
      >
        <LanguageSelector
          currentLanguage={language}
          handleChangeLanguage={handleLanguageChange}
          withAnimation={true}
        />
      </div>
    </div>
  );
};

export default LoginPage;
